import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, lastValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { sortArrayOfObject } from '../../../../global.variable';
import { gTDB } from '../../../shared/services/db';
import { DeviceInfoService } from '../../../shared/services/device-info.service';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  cachedIndexDbData: { [key: string]: { data: any[]; expires: number } } = {};
  constructor(
    private http: HttpClient,
    private deviceInfoService: DeviceInfoService,
  ) {}

  async addCompany(requestData: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post('/api/company/', body)
        .pipe(map((response: any) => response)),
    );
  }
  async updateCompanyDetails(requestData?: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post('/api/company_profile/', body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async updateSMTPConfig(requestData?: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post('/api/company_profile/update_smtp_details/', body, {
          params: params,
        })
        .pipe(map((response) => response)),
    );
  }
  async addCompanyLicense(requestData: any) {
    const body = {
      ...requestData,
    };
    return this.http
      .post('/api/company_profile/add_subscriber_license/', body)
      .pipe(map((response) => response));
  }

  async updateCompanyDetailsById(requestData: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(`/api/company/${requestData.id}/`, body)
        .pipe(map((response) => response)),
    );
  }
  async enableDisableCompanyById(id: any) {
    let body = {
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .patch(`/api/company/${id}/`, body)
        .pipe(map((response) => response)),
    );
  }
  async enableDisableSiteById(id: any) {
    let body = {
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .patch(`/api/site/${id}/`, body)
        .pipe(map((response) => response)),
    );
  }
  async addCustomerContactById(requestData: any) {
    const body = {
      ...requestData.data,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(`/api/company/${requestData.id}/add_contact/`, body)
        .pipe(map((response) => response)),
    );
  }
  async addSiteContactById(requestData: any) {
    const body = {
      ...requestData.data,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(`/api/site/${requestData.id}/add_contact/`, body)
        .pipe(map((response) => response)),
    );
  }
  fetchCustomerList(params?: any) {
    return this.http
      .get('/api/company/', { params: params })
      .pipe(map((response) => response));
  }
  getCompanyDetailById(id: any, params?: any) {
    return this.http.get(`/api/company/${id}/`, { params: params });
  }

  async updateClient(id: any, body: any, params?: any) {
    body = {
      ...body,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post(`/api/company/${id}/update/`, body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async updateSite(id: any, body: any, params?: any) {
    body = {
      ...body,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };

    return lastValueFrom(
      this.http
        .post(`/api/site/${id}/update/`, body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async modifySiteKey(id: any, body: any, params?: any, addGps = false) {
    if (addGps) {
      body.device_info = await this.deviceInfoService.getDeviceDetail();
    }
    return lastValueFrom(
      this.http
        .post(`/api/site/${id}/modify_site_key/`, body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async listSiteKeys(params?: any) {
    return lastValueFrom(
      this.http
        .get(`/api/site/list_site_keys/`, { params: params })
        .pipe(map((response) => response)),
    );
  }

  getCustomerReport(id: any, requestData: any) {
    return this.http.post(`/api/company/${id}/generate_report/`, requestData);
  }
  getSiteContacts(id: any, params?: any) {
    return this.http.get(`/api/site/${id}/contacts/`, { params: params });
  }
  searchLocationContacts(params: any) {
    return this.http.get(`/api/contacts/`, { params: params });
  }

  updateLocationContact(id: any, body: any, params?: any) {
    return this.http
      .put(`/api/contacts/${id}/`, body, { params: params })
      .pipe(map((response) => response));
  }
  searchSites(params?: any) {
    const searchString = params?.search_str;

    return from(this.fetchClientORSitesFromIndexedDb(searchString, true)).pipe(
      switchMap((dataFromIndexedDb: any) => {
        if (dataFromIndexedDb && dataFromIndexedDb?.length > 0) {
          return of({ status: 'success', data: dataFromIndexedDb });
        } else {
          return this.http
            .get('/api/site/', { params: params })
            .pipe(map((response) => response));
        }
      }),
    );
  }
  getSiteList(params: any) {
    return this.http.get(`api/site/`, { params: params });
  }

  searchClients(searchString: any) {
    return from(this.fetchClientORSitesFromIndexedDb(searchString)).pipe(
      switchMap((dataFromIndexedDb: any) => {
        if (dataFromIndexedDb && dataFromIndexedDb?.length > 0) {
          return of({ status: 'success', data: dataFromIndexedDb });
        } else {
          return this.http
            .get('/api/company/', { params: { search_str: searchString } })
            .pipe(map((response) => response));
        }
      }),
    );
  }

  async addSiteInClient(requestData: any) {
    const body = {
      ...requestData.data,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http.post(`/api/site/`, body).pipe(map((response) => response)),
    );
  }
  async updateSiteById(requestData: any, id: any) {
    const body = {
      ...requestData.data,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http.put(`api/site/${id}/`, body).pipe(map((response) => response)),
    );
  }
  getSiteDetailsById(id: any, params?: any) {
    return this.http.get(`api/site/${id}/`, { params });
  }
  async deleteClientContact(id: any, contactId: any, contact_type: any) {
    let body = {
      contact_id: contactId,
      contact_type: contact_type,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http.put(`api/company/${id}/delete_contact/`, body),
    );
  }
  async deleteSiteContact(id: any, contactId: any, contact_type: any) {
    let body = {
      contact_id: contactId,
      contact_type: contact_type,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(this.http.put(`api/site/${id}/delete_contact/`, body));
  }

  editLocationContact(id: any, params?: any) {
    return this.http.get(`api/contacts/${id}/`, {
      params: params,
    });
  }

  downloadTemplateFile() {
    const downloadHttpOption = {
      responseType: 'blob' as 'json',
    };
    return this.http.get(`/api/file_import/`, downloadHttpOption);
  }

  //Monitoring Companies related APIs
  fetchMonitoringCompanyList(params?: any) {
    return this.http
      .get('/api/monitoring_company/', { params: params })
      .pipe(map((response) => response));
  }
  fetchMonitoringCompanyById(id?: any) {
    return this.http
      .get(`/api/monitoring_company/${id}/`)
      .pipe(map((response) => response));
  }
  async createMonitoringCompany(requestData?: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post('/api/monitoring_company/', body)
        .pipe(map((response) => response)),
    );
  }
  async updateMonitoringCompanyDetailById(requestData: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(`/api/monitoring_company/${requestData.id}/`, body, {
          params: params,
        })
        .pipe(map((response) => response)),
    );
  }
  async deleteMonitoringCompany(id: any, params?: any) {
    const body = {
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http.put(`api/monitoring_company/${id}/`, body, {
        params: params,
      }),
    );
  }

  //Response Type related APIs
  fetchResponseTypeList(params?: any) {
    return this.http
      .get('/api/response_type/', { params: params })
      .pipe(map((response) => response));
  }
  async createResponseType(requestData?: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post('/api/response_type/', body)
        .pipe(map((response) => response)),
    );
  }
  async updateResponseTypeDetailById(requestData: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(`/api/response_type/${requestData.id}/`, body)
        .pipe(map((response) => response)),
    );
  }
  async deleteResponseType(id: any, params?: any) {
    const body = {
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http.put(`api/response_type/${id}/`, body, {
        params: params,
      }),
    );
  }
  addContactSubscriberUser(requestData: any) {
    const body = {
      ...requestData.data,
    };
    return this.http
      .post(`/api/contacts/`, body)
      .pipe(map((response) => response));
  }
  async fetchClientORSitesFromIndexedDb(
    searchStr: string,
    isSite: boolean = false,
  ) {
    const path = isSite
      ? '/api/site/list_all_sites/'
      : '/api/company/list_all_companies/';

    try {
      const cachedData = this.cachedIndexDbData[path] || {};
      const now = Date.now();
      const cacheDuration = 2 * 60 * 1000;

      if (!cachedData?.data?.length || cachedData?.expires < now) {
        const record: any = await gTDB.cachedData
          .where('shortPath')
          .equals(path)
          .first();

        const rawData: any[] = record?.data || [];

        this.cachedIndexDbData[path] = {
          data: rawData.map((item) => ({
            ...item,
            __lcCompanyName: item.company_name?.toLowerCase()?.trim() || '',
            __lcCompanyCompanyName:
              item.company?.company_name?.toLowerCase() || '',
            __lcAddress: item.address?.full_address?.toLowerCase() || '',
          })),
          expires: now + cacheDuration,
        };
      }
      const resultData = [...(this.cachedIndexDbData[path]?.data || [])];

      if (!searchStr)
        return sortArrayOfObject(resultData, '__lcCompanyName', 'asc');

      searchStr = searchStr
        ?.trim()
        ?.toLowerCase()
        ?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const itemBucket1: any = [];
      const itemBucket2: any = [];
      const itemBucket3: any = [];
      const regex = new RegExp(`\\b${searchStr}`, 'i');

      const startsWithSearch = (field = '') => regex.test(field);

      const sortByCreatedAt = (arr: any) =>
        arr?.sort((a: any, b: any) => {
          if (!a?.created_at && !b?.created_at) return 0;
          if (!a?.created_at) return 1;
          if (!b?.created_at) return -1;

          return (
            new Date(b?.created_at).getTime() -
            new Date(a?.created_at).getTime()
          );
        });

      resultData.forEach((item) => {
        if (startsWithSearch(item.__lcCompanyName)) {
          itemBucket1.push(item);
        } else if (startsWithSearch(item.__lcAddress)) {
          itemBucket2.push(item);
        } else if (isSite && startsWithSearch(item.__lcCompanyCompanyName)) {
          itemBucket3.push(item);
        }
      });

      // sortByCreatedAt(itemBucket1);
      // sortByCreatedAt(itemBucket2);
      // sortByCreatedAt(itemBucket3);
      sortArrayOfObject(itemBucket1, '__lcCompanyName', 'asc');
      sortArrayOfObject(itemBucket2, '__lcCompanyName', 'asc');
      sortArrayOfObject(itemBucket3, '__lcCompanyName', 'asc');

      const seen = new Set();
      const concatenated = [...itemBucket1, ...itemBucket2, ...itemBucket3];

      const filteredList = concatenated.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });

      return filteredList;
    } catch (error) {
      return [];
    }
  }

  //Service Type related APIs
  fetchServiceTypeList(params?: any) {
    return this.http
      .get('/api/service_type/', { params: params })
      .pipe(map((response) => response));
  }
  async createServiceType(requestData?: any, params?: any) {
    const body = {
      ...requestData,
    };
    return lastValueFrom(
      this.http
        .post('/api/service_type/', body)
        .pipe(map((response) => response)),
    );
  }
  async updateServiceTypeDetailById(requestData: any) {
    const body = {
      ...requestData,
    };
    return lastValueFrom(
      this.http
        .put(`/api/service_type/${requestData.id}/`, body)
        .pipe(map((response) => response)),
    );
  }
  async deleteServiceType(id: any, params?: any) {
    const body = {};
    return lastValueFrom(
      this.http.put(`api/service_type/${id}/`, body, {
        params: params,
      }),
    );
  }
  mangeKeyAudit(body?: any, params?: any) {
    return lastValueFrom(
      this.http.post(`api/site/key_audit/`, body, {
        params: params,
      }),
    );
  }

  createScheduledPatrolReprots(body: any, params: any) {
    return this.http.post(`/api/scheduled_report/`, body, { params: params });
  }
  listScheduledPatrolReprots(params: any) {
    return this.http.get(`/api/scheduled_report/`, { params: params });
  }
  getScheduledPatrolReprotById(id: string, params: any) {
    return this.http.get(`/api/scheduled_report/${id}/`, { params: params });
  }

  updateScheduledPatrolReprotById(id: string, body: any, params: any) {
    return this.http.put(`/api/scheduled_report/${id}/`, body, {
      params: params,
    });
  }
  getJobQueuedReports(params: any) {
    return this.http.get(`/api/scheduled_report/check_job_in_schedules/`, {
      params: params,
    });
  }
  async sitePreferredUser(body?: any, params?: any) {
    return lastValueFrom(
      this.http
        .post(`/api/site/site_preferred_users/`, body, {
          params: params,
        })
        .pipe(map((response) => response)),
    );
  }
  getPatrolReportLog(requestData?: any, params?: any) {
    const body = { ...requestData };

    return this.http.post('/api/patrol_report/', body, { params: params });
  }
  async exportPatrolReportLog(requestData?: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };

    return lastValueFrom(
      this.http
        .post('/api/patrol_report/', body, { params: params })
        .pipe(map((response) => response)),
    );
  }
}
